/**
 * Ready-made data for the simulator blocks, taken from the AI Essentials lessons they were
 * built for. The lesson builder uses them as the starting point for a new block.
 */

/** Lesson 1, "What is AI?": try to fool a one-rule spam filter and a learned one. */
export const SPAM_FILTER_EXAMPLE = {
  title: "Try to fool both filters",
  prompt: "Pick an email subject line below. Watch which filter catches it.",
  placeholder: "Type a subject line…",
  examples: [
    "FREE MONEY inside",
    "Fr33 m0ney waiting for you!!",
    "Congratulations! Claim your prize now",
    "Lunch meeting tomorrow at 1pm?",
    "Your invoice for September",
  ],
  positive_label: "Spam",
  negative_label: "Inbox",
  rule: {
    title: "Rule-based filter",
    description: 'Its only rule: if the subject contains "free money", it\'s spam.',
    phrase: "free money",
    lookalike_pattern: "fr[e3]{2}|m[o0]n[e3]y|prize|claim|lottery|winner",
  },
  learned: {
    title: "Learned filter",
    description: "It weighs many patterns it picked up from examples.",
    weights: {
      free: 1.2, money: 1.3, prize: 1.6, claim: 1.3, winner: 1.6, won: 1.2, congratulations: 1.2, urgent: 1.2,
      click: 1.0, now: 0.6, lottery: 1.8, cash: 1.2, offer: 0.8, limited: 0.8, guaranteed: 1.2, act: 0.5, bonus: 1.0,
      waiting: 0.5, meeting: -1.2, lunch: -1.1, tomorrow: -0.6, invoice: -0.5, report: -0.9, thanks: -0.9, class: -0.8,
      agenda: -1.1, minutes: -0.6, assignment: -0.9,
    },
    exclaim_weight: 0.45,
    exclaim_max: 3,
    caps_weight: 0.5,
    caps_max: 2,
    threshold: 1.4,
    steepness: 1.6,
  },
  footnote:
    "This is a simplified simulation. Real spam filters learn from billions of emails and look at far more than the subject line.",
};

/** Lesson 3, "How AI learns": label messages, train a model on them, test it. */
export const TRAIN_MODEL_EXAMPLE = {
  labels: [
    { id: "u", label: "Urgent" },
    { id: "w", label: "Can wait" },
  ],
  training: [
    { id: "t1", text: "The client website is down, please fix it now", label_id: "u" },
    { id: "t2", text: "Sharing some photos from the team lunch", label_id: "w" },
    { id: "t3", text: "Deadline moved to today, need the report urgently", label_id: "u" },
    { id: "t4", text: "No rush, but have a look at this idea next week", label_id: "w" },
    { id: "t5", text: "Payment failed and the customer is waiting on the phone", label_id: "u" },
    { id: "t6", text: "Monthly newsletter: new courses this season", label_id: "w" },
    { id: "t7", text: "Server crashed, nobody can log in right now", label_id: "u" },
    { id: "t8", text: "When you get a chance, update your profile picture", label_id: "w" },
    { id: "t9", text: "Urgent: sign the contract before 5pm today", label_id: "u" },
    { id: "t10", text: "Sometime next month let's plan a team outing", label_id: "w" },
  ],
  test: [
    { id: "x1", text: "The booking page is down and customers are waiting", label_id: "u" },
    { id: "x2", text: "Need your approval today before the deadline", label_id: "u" },
    { id: "x3", text: "Here are some ideas for next week's workshop, no rush", label_id: "w" },
    { id: "x4", text: "Newsletter photos from last month's event", label_id: "w" },
    { id: "x5", text: "Login failed for the whole team, fix it right now", label_id: "u" },
    { id: "x6", text: "When you get a chance, look at the new course ideas", label_id: "w" },
  ],
  experiments: true,
  swap_ids: ["t1", "t4", "t5"],
};

/** Lesson 3, "How AI represents meaning": words that mean similar things sit together. */
export const MEANING_MAP_EXAMPLE = {
  caption: "A meaning map: similar meanings sit close together",
  groups: [
    { id: "fruit", label: "fruit" },
    { id: "transport", label: "transport" },
    { id: "feelings", label: "feelings" },
    { id: "cities", label: "cities" },
  ],
  words: [
    { id: "mango", label: "mango", group_id: "fruit" },
    { id: "banana", label: "banana", group_id: "fruit" },
    { id: "apple", label: "apple", group_id: "fruit" },
    { id: "dates", label: "dates", group_id: "fruit" },
    { id: "car", label: "car", group_id: "transport" },
    { id: "bus", label: "bus", group_id: "transport" },
    { id: "rickshaw", label: "rickshaw", group_id: "transport" },
    { id: "train", label: "train", group_id: "transport" },
    { id: "happy", label: "happy", group_id: "feelings" },
    { id: "joyful", label: "joyful", group_id: "feelings" },
    { id: "glad", label: "glad", group_id: "feelings" },
    { id: "sad", label: "sad", group_id: "feelings" },
    { id: "karachi", label: "Karachi", group_id: "cities" },
    { id: "lahore", label: "Lahore", group_id: "cities" },
    { id: "riyadh", label: "Riyadh", group_id: "cities" },
    { id: "islamabad", label: "Islamabad", group_id: "cities" },
  ],
  notes: [
    {
      word_id: "sad",
      text: 'Notice "sad" sits with the happy words even though it means the opposite. They\'re used in the same kinds of sentences ("I feel …"), so the AI places them close together.',
    },
  ],
};
